import React, { useState, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import Select from 'react-select';
import api from '../../../../config/axios';
import Swal from 'sweetalert2';
import DatePicker, { dateHelpers } from '../../../../components/utils/datepicker';

import { autoCompleteClientes, autoCompleteFornecedores } from '../../../../components/utils/autocompletes';

import { FaRegSave, FaTimes } from 'react-icons/fa';

const NovoLancamento = ({ onClose, onLancamentoAtualizado }) => {
    const [formData, setFormData] = useState({
        tipo: 'receita',
        categoria_id: '',
        valor: '',
        data_vencimento: '',
        data_competencia: '',
        data_lancamento: new Date().toLocaleDateString('pt-BR'),
        centro_custo_id: '',
        pessoa_cliente_id: '',
        numero_doc: '',
        observacao: '',
        descricao: '',
        repeticao: 'unica',
        conta_bancaria_id: '',
        forma_pagamento: 'nenhum',
        tipo_transferencia: '',
        conta_origem_id: '',
        conta_destino_id: '',
        parcelas: 2,
        repeticao_quantidade: 2,
        repeticao_periodo: 'mensal',
        bandeira_id: ''
    });


    const [categorias, setCategorias] = useState([]);
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [contasBancarias, setContasBancarias] = useState([]);
    const [selectedPessoa, setSelectedPessoa] = useState(null);
    const [bandeirasCartao, setBandeirasCartao] = useState([]);
    const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);

    const { inputPesquisaCliente, clientesOptions, handleAutoCompleteClientes, handleFocusClientes } = autoCompleteClientes();
    const { inputPesquisaFornecedor, fornecedoresOptions, handleAutoCompleteFornecedores } = autoCompleteFornecedores();

    useEffect(() => {
        fetchCategorias();
        fetchCentrosCusto();
        fetchContasBancarias();
        fetchTags();
    }, []);

    const fetchTags = async () => {
        try {
            const response = await api.get('/tags/ativas');
            setTagsDisponiveis(response.data || []);
        } catch (error) {
            setTagsDisponiveis([]);
        }
    };

    const fetchCategorias = async (tipo = null) => {
        try {
            const tipoParam = tipo || formData.tipo;
            let tipoFiltro;

            const userData = JSON.parse(localStorage.getItem('userData') || '{}');
            const tipoVinculo = userData.unidade_designacao_id ? 'unidade' : 'gestora';

            if (tipoParam === 'receita') {
                tipoFiltro = 'receita';
            } else if (tipoParam === 'despesa') {
                tipoFiltro = 'despesa';
            } else {
                const response = await api.get(`/categorias-financeiras?limit=200&tipo_vinculo=${tipoVinculo}`);
                setCategorias(response.data.records || []);
                return;
            }

            const response = await api.get(`/categorias-financeiras?limit=200&tipo_financeiro=${tipoFiltro}&tipo_vinculo=${tipoVinculo}`);
            setCategorias(response.data.records || []);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar categorias', 'error');
        }
    };

    const fetchCentrosCusto = async () => {
        try {
            const response = await api.get('/centros-de-custo?limit=200');
            setCentrosCusto(response.data.records || []);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar centros de custo', 'error');
        }
    };

    const fetchContasBancarias = async () => {
        try {
            const response = await api.get('/contas-bancarias/para-lancamentos');
            setContasBancarias(response.data || []);
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar contas bancárias', 'error');
        }
    };

    const fetchBandeirasCartao = async (contaBancariaId, formaPagamento) => {
        try {
            if (formaPagamento === 'cartao_credito') {
                const responseAvista = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_avista&limit=1000`);
                const responseParcelado = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=credito_parcelado&limit=1000`);

                const bandeirasAvista = (responseAvista.data.records || []).map(b => ({ ...b, tipo_exibicao: 'À Vista' }));
                const bandeirasParcelado = (responseParcelado.data.records || []).map(b => ({ ...b, tipo_exibicao: 'Parcelado' }));

                return [...bandeirasAvista, ...bandeirasParcelado];
            } else if (formaPagamento === 'cartao_debito') {
                const response = await api.get(`/cartoes?conta_bancaria_id=${contaBancariaId}&tipo=debito&limit=1000`);
                return response.data.records || [];
            }
            return [];
        } catch (error) {
            return [];
        }
    };

    useEffect(() => {
        const carregarBandeiras = async () => {
            const contaId = formData.tipo === 'transferencia' ? formData.conta_origem_id : formData.conta_bancaria_id;

            if (contaId && (formData.forma_pagamento === 'cartao_credito' || formData.forma_pagamento === 'cartao_debito')) {
                const bandeiras = await fetchBandeirasCartao(contaId, formData.forma_pagamento);
                setBandeirasCartao(bandeiras);
            } else {
                setBandeirasCartao([]);
                setFormData(prev => ({
                    ...prev,
                    bandeira_id: ''
                }));
            }
        };

        carregarBandeiras();
    }, [formData.conta_bancaria_id, formData.conta_origem_id, formData.forma_pagamento, formData.tipo]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTipoChange = (e) => {
        const tipo = e.target.value;
        setFormData(prev => ({
            ...prev,
            tipo: tipo,
            categoria_id: '',
            centro_custo_id: '',
            tipo_transferencia: '',
            conta_origem_id: '',
            conta_destino_id: ''
        }));
        if (tipo !== 'transferencia') {
            fetchCategorias(tipo);
        }
    };

    const convertToISO = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('-')) return dateString;
        if (dateString.length !== 10) return '';
        const [dia, mes, ano] = dateString.split('/');
        return `${ano}-${mes}-${dia}`;
    };

    const convertFromISO = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('/')) return dateString;
        const [ano, mes, dia] = dateString.split('-');
        return `${dia}/${mes}/${ano}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const valorNumerico = parseFloat(formData.valor.replace(/\./g, '').replace(',', '.'));

        if (!formData.valor || valorNumerico <= 0) {
            Swal.fire('Erro', 'O valor deve ser maior que zero', 'error');
            return;
        }

        if (!formData.data_lancamento) {
            Swal.fire('Erro', 'A data de lançamento é obrigatória', 'error');
            return;
        }

        if (formData.tipo === 'transferencia') {
            if (!formData.conta_origem_id || !formData.conta_destino_id) {
                Swal.fire('Erro', 'Selecione a conta de origem e a conta de destino', 'error');
                return;
            }

            if (!formData.tipo_transferencia) {
                Swal.fire('Erro', 'Selecione o tipo de transferência', 'error');
                return;
            }

            if (formData.conta_origem_id === formData.conta_destino_id) {
                Swal.fire('Erro', 'A conta de origem deve ser diferente da conta de destino', 'error');
                return;
            }
        } else {
            if (!formData.data_competencia) {
                Swal.fire('Erro', 'A data de competência é obrigatória', 'error');
                return;
            }

            if (!formData.data_vencimento) {
                Swal.fire('Erro', 'A data de vencimento é obrigatória', 'error');
                return;
            }

            if (!formData.categoria_id) {
                Swal.fire('Erro', 'Selecione uma categoria', 'error');
                return;
            }

            if (categorias.length === 0) {
                Swal.fire('Erro', 'Não há categorias disponíveis para o tipo de lançamento selecionado', 'error');
                return;
            }

            if (formData.tipo === 'despesa' && !formData.centro_custo_id) {
                Swal.fire('Erro', 'Selecione um centro de custo', 'error');
                return;
            }

            if (!formData.conta_bancaria_id) {
                Swal.fire('Erro', 'Selecione uma conta bancária', 'error');
                return;
            }
        }

        if (formData.repeticao === 'parcelado') {
            const parcelas = parseInt(formData.parcelas);
            if (parcelas < 2 || parcelas > 24) {
                Swal.fire('Erro', 'O número de parcelas deve ser entre 2 e 24', 'error');
                return;
            }
        }

        if (formData.repeticao === 'fixo') {
            const quantidade = parseInt(formData.repeticao_quantidade);
            if (quantidade < 1 || quantidade > 99) {
                Swal.fire('Erro', 'A quantidade de repetições deve ser entre 1 e 99', 'error');
                return;
            }
        }

        if (formData.descricao && formData.descricao.length > 500) {
            Swal.fire('Erro', 'A descrição deve ter no máximo 500 caracteres', 'error');
            return;
        }

        if (formData.observacao && formData.observacao.length > 1000) {
            Swal.fire('Erro', 'A observação deve ter no máximo 1000 caracteres', 'error');
            return;
        }

        try {
            Swal.fire({
                title: 'Carregando...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const dataToSend = {
                valor: parseFloat(formData.valor.replace(/\./g, '').replace(',', '.')),
                data_lancamento: convertToISO(formData.data_lancamento),
                descricao: formData.descricao || null,
                numero_doc: formData.numero_doc || null,
                observacao: formData.observacao || null,
                tipo_lancamento: formData.repeticao === 'unica' ? 'unico' : formData.repeticao,
                conta_bancaria_id: formData.conta_bancaria_id ? parseInt(formData.conta_bancaria_id) : null,
                forma_pagamento: formData.forma_pagamento !== 'nenhum' ? formData.forma_pagamento : null,
                bandeira_id: formData.bandeira_id ? parseInt(formData.bandeira_id) : null
            };

            if (formData.tipo === 'transferencia') {
                dataToSend.tipo_transferencia = formData.tipo_transferencia;
                dataToSend.conta_origem_id = parseInt(formData.conta_origem_id);
                dataToSend.conta_destino_id = parseInt(formData.conta_destino_id);
            } else {
                dataToSend.categoria_id = parseInt(formData.categoria_id);
                dataToSend.data_vencimento = convertToISO(formData.data_vencimento);
                dataToSend.data_competencia = convertToISO(formData.data_competencia) || convertToISO(formData.data_vencimento);
                dataToSend.centro_custo_id = formData.centro_custo_id ? parseInt(formData.centro_custo_id) : null;
                dataToSend.pessoa_cliente_id = formData.pessoa_cliente_id ? parseInt(formData.pessoa_cliente_id) : null;
            }

            if (formData.repeticao === 'parcelado') {
                dataToSend.parcela_total = parseInt(formData.parcelas);
            } else if (formData.repeticao === 'fixo') {
                dataToSend.repeticao_quantidade = parseInt(formData.repeticao_quantidade);
                dataToSend.repeticao_periodo = formData.repeticao_periodo;
            }

            if (selectedTags.length > 0) {
                dataToSend.tags_ids = selectedTags.map(tag => tag.value);
            }

            await api.post('/lancamentos', dataToSend);

            Swal.fire('Sucesso', 'Lançamento criado com sucesso', 'success');
            onLancamentoAtualizado();
            handleClose();
        } catch (error) {
            Swal.fire('Erro', error.response?.data?.message || 'Erro ao criar lançamento', 'error');
        }
    };

    const handleClose = () => {
        setFormData({
            tipo: 'receita',
            categoria_id: '',
            valor: '',
            data_vencimento: '',
            data_competencia: '',
            data_lancamento: new Date().toLocaleDateString('pt-BR'),
            centro_custo_id: '',
            pessoa_cliente_id: '',
            numero_doc: '',
            observacao: '',
            descricao: '',
            repeticao: 'unica',
            conta_bancaria_id: '',
            forma_pagamento: 'nenhum',
            tipo_transferencia: '',
            conta_origem_id: '',
            conta_destino_id: '',
            parcelas: 1,
            repeticao_quantidade: 1,
            repeticao_periodo: 'mensal',
            bandeira_id: ''
        });
        setSelectedPessoa(null);
        setSelectedTags([]);
        onClose();
    };

    const handlePessoaSelect = (selectedOption) => {
        setSelectedPessoa(selectedOption);
        setFormData(prev => ({
            ...prev,
            pessoa_cliente_id: selectedOption ? selectedOption.value : ''
        }));
    };

    const getContrastColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Novo Lançamento</h4>
                        <button type="button" className="btn-close" onClick={handleClose}></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Tipo de Lançamento</label>
                                    <select
                                        className="form-select"
                                        name="tipo"
                                        value={formData.tipo}
                                        onChange={handleTipoChange}
                                        required
                                    >
                                        <option value="receita">Receita</option>
                                        <option value="despesa">Despesa</option>
                                        <option value="transferencia">Transferência</option>
                                    </select>
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">
                                        {formData.repeticao === 'parcelado' ? 'Valor Total' : 'Valor'}
                                    </label>
                                    <CurrencyInput
                                        name="valor"
                                        placeholder="R$ 0,00"
                                        value={formData.valor}
                                        decimalsLimit={2}
                                        decimalSeparator=","
                                        groupSeparator="."
                                        prefix="R$ "
                                        className="form-control"
                                        autoComplete="off"
                                        onValueChange={(value) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                valor: value || ''
                                            }));
                                        }}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Data de Lançamento</label>
                                    <DatePicker
                                        selected={dateHelpers.stringToDate(formData.data_lancamento)}
                                        onChange={dateHelpers.createOnChangeHandler((date) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                data_lancamento: date
                                            }));
                                        })}
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Repetição</label>
                                    <select
                                        className="form-select"
                                        name="repeticao"
                                        value={formData.repeticao}
                                        onChange={handleInputChange}
                                    >
                                        <option value="unica">Única</option>
                                        <option value="parcelado">Parcelada</option>
                                        <option value="fixo">Fixa</option>
                                    </select>
                                </div>
                                {formData.repeticao === 'parcelado' && (
                                    <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                        <label className="form-label fw-bold">Número de Parcelas</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="parcelas"
                                            value={formData.parcelas}
                                            onChange={handleInputChange}
                                            min="2"
                                            max="24"
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            {formData.repeticao === 'fixo' && (
                                <div className="row mt-3">
                                    <div className="col-lg-4 col-12">
                                        <label className="form-label fw-bold">Quantidade</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="repeticao_quantidade"
                                            value={formData.repeticao_quantidade}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="99"
                                            required
                                        />
                                    </div>
                                    <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                        <label className="form-label fw-bold">Período</label>
                                        <select
                                            className="form-select"
                                            name="repeticao_periodo"
                                            value={formData.repeticao_periodo}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="semanal">Semanal</option>
                                            <option value="quinzenal">Quinzenal</option>
                                            <option value="mensal">Mensal</option>
                                            <option value="bimestral">Bimestral</option>
                                            <option value="trimestral">Trimestral</option>
                                            <option value="semestral">Semestral</option>
                                            <option value="anual">Anual</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Descrição</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="descricao"
                                        value={formData.descricao}
                                        autoComplete="off"
                                        maxLength={500}
                                        onChange={handleInputChange}
                                    />
                                    <small className="text-muted">{formData.descricao.length}/500</small>
                                </div>
                                {formData.tipo !== 'transferencia' ? (
                                    <>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Data de Competência</label>
                                            <DatePicker
                                                selected={dateHelpers.stringToDate(formData.data_competencia)}
                                                onChange={dateHelpers.createOnChangeHandler((date) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        data_competencia: date
                                                    }));
                                                })}
                                            />
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Data de Vencimento</label>
                                            <DatePicker
                                                selected={dateHelpers.stringToDate(formData.data_vencimento)}
                                                onChange={dateHelpers.createOnChangeHandler((date) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        data_vencimento: date
                                                    }));
                                                })}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                        <label className="form-label fw-bold">Tipo de Transferência</label>
                                        <select
                                            className="form-select"
                                            name="tipo_transferencia"
                                            value={formData.tipo_transferencia}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="" hidden>Selecione...</option>
                                            <option value="aplicacao">Aplicação</option>
                                            <option value="aprovisionamento">Aprovisionamento</option>
                                            <option value="pagamento_cartao">Pagamento de Cartão</option>
                                            <option value="resgate">Resgate</option>
                                            <option value="saque">Saque</option>
                                            <option value="transferencia">Transferência</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="row mt-3">
                                {formData.tipo !== 'transferencia' ? (
                                    <>
                                        <div className="col-lg-4 col-12">
                                            <label className="form-label fw-bold">Categoria</label>
                                            <select
                                                className="form-select"
                                                name="categoria_id"
                                                value={formData.categoria_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="" hidden>Selecione...</option>
                                                {categorias.map(categoria => (
                                                    <option key={categoria.id} value={categoria.id}>
                                                        {categoria.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Centro de Custo</label>
                                            <select
                                                className="form-select"
                                                name="centro_custo_id"
                                                value={formData.centro_custo_id}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Selecione...</option>
                                                {centrosCusto.map(centro => (
                                                    <option key={centro.id} value={centro.id}>
                                                        {centro.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">
                                                {formData.tipo === 'receita' ? 'Cliente' : 'Fornecedor'}
                                            </label>
                                            <Select
                                                value={selectedPessoa}
                                                onChange={handlePessoaSelect}
                                                onInputChange={(value) => {
                                                    if (formData.tipo === 'receita') {
                                                        handleAutoCompleteClientes(value);
                                                    } else {
                                                        handleAutoCompleteFornecedores(value);
                                                    }
                                                }}
                                                onFocus={() => {
                                                    if (formData.tipo === 'receita') {
                                                        handleFocusClientes();
                                                    }
                                                }}
                                                options={formData.tipo === 'receita' ? clientesOptions : fornecedoresOptions}
                                                isSearchable
                                                isClearable
                                                placeholder={`Buscar ${formData.tipo === 'receita' ? 'cliente' : 'fornecedor'}...`}
                                                noOptionsMessage={() => 'Nenhum resultado encontrado'}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="col-lg-5 col-12">
                                            <label className="form-label fw-bold">Conta Origem</label>
                                            <select
                                                className="form-select"
                                                name="conta_origem_id"
                                                value={formData.conta_origem_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="" hidden>Selecione...</option>
                                                {contasBancarias.map(conta => (
                                                    <option key={conta.id} value={conta.id}>
                                                        {conta.nome_exibicao || conta.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-2 d-flex align-items-end justify-content-center">
                                            <div className="text-center fw-bold d-none d-lg-block" style={{fontSize: '1.5rem'}}>→</div>
                                            <div className="text-center fw-bold d-block d-lg-none" style={{fontSize: '1.5rem'}}>↓</div>
                                        </div>
                                        <div className="col-lg-5 col-12">
                                            <label className="form-label fw-bold">Conta Destino</label>
                                            <select
                                                className="form-select"
                                                name="conta_destino_id"
                                                value={formData.conta_destino_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="" hidden>Selecione...</option>
                                                {contasBancarias
                                                    .filter(conta => conta.id !== parseInt(formData.conta_origem_id))
                                                    .map(conta => (
                                                    <option key={conta.id} value={conta.id}>
                                                        {conta.nome_exibicao || conta.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="row mt-3">
                                {formData.tipo !== 'transferencia' ? (
                                    <>
                                        <div className="col-lg-4 col-12">
                                            <label className="form-label fw-bold">Conta Bancária</label>
                                            <select
                                                className="form-select"
                                                name="conta_bancaria_id"
                                                value={formData.conta_bancaria_id}
                                                onChange={handleInputChange}
                                            >
                                                <option value="" hidden>Selecione...</option>
                                                {contasBancarias.map(conta => (
                                                    <option key={conta.id} value={conta.id}>
                                                        {conta.nome_exibicao || conta.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Forma de Pagamento</label>
                                            <select
                                                className="form-select"
                                                name="forma_pagamento"
                                                value={formData.forma_pagamento}
                                                onChange={handleInputChange}
                                            >
                                                <option value="nenhum">Nenhum</option>
                                                <option value="cartao_credito">Cartão de Crédito</option>
                                                <option value="cartao_debito">Cartão de Débito</option>
                                                <option value="cheque">Cheque</option>
                                                <option value="pix">PIX</option>
                                                <option value="dinheiro">Dinheiro</option>
                                                <option value="boleto">Boleto</option>
                                                <option value="ted">TED</option>
                                                <option value="doc">DOC</option>
                                            </select>
                                        </div>
                                        {(formData.forma_pagamento === 'cartao_credito' || formData.forma_pagamento === 'cartao_debito') && formData.conta_bancaria_id && (
                                            <div className="col-lg-4 col-12 mt-lg-0 mt-3 mb-0 mb-lg-3">
                                                <label className="form-label fw-bold">Bandeira</label>
                                                <select
                                                    className="form-select"
                                                    name="bandeira_id"
                                                    value={formData.bandeira_id}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {bandeirasCartao.map(bandeira => (
                                                        <option key={bandeira.id} value={bandeira.id}>
                                                            {bandeira.bandeira}{bandeira.tipo_exibicao ? ` - ${bandeira.tipo_exibicao}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Número do Documento/Nota Fiscal</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="numero_doc"
                                                value={formData.numero_doc}
                                                autoComplete="off"
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="col-lg-4">
                                            <label className="form-label fw-bold">Forma de Pagamento</label>
                                            <select
                                                className="form-select"
                                                name="forma_pagamento"
                                                value={formData.forma_pagamento}
                                                onChange={handleInputChange}
                                            >
                                                <option value="nenhum">Nenhum</option>
                                                <option value="cartao_credito">Cartão de Crédito</option>
                                                <option value="cartao_debito">Cartão de Débito</option>
                                                <option value="cheque">Cheque</option>
                                                <option value="pix">PIX</option>
                                                <option value="dinheiro">Dinheiro</option>
                                                <option value="boleto">Boleto</option>
                                                <option value="ted">TED</option>
                                                <option value="doc">DOC</option>
                                            </select>
                                        </div>
                                        {(formData.forma_pagamento === 'cartao_credito' || formData.forma_pagamento === 'cartao_debito') && formData.conta_origem_id && (
                                            <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                                <label className="form-label fw-bold">Bandeira</label>
                                                <select
                                                    className="form-select"
                                                    name="bandeira_id"
                                                    value={formData.bandeira_id}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {bandeirasCartao.map(bandeira => (
                                                        <option key={bandeira.id} value={bandeira.id}>
                                                            {bandeira.bandeira}{bandeira.tipo_exibicao ? ` - ${bandeira.tipo_exibicao}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Número do Documento/Nota Fiscal</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="numero_doc"
                                                value={formData.numero_doc}
                                                autoComplete="off"
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-6 col-12">
                                    <label className="form-label fw-bold">Tags</label>
                                    <Select
                                        isMulti
                                        placeholder="Selecione..."
                                        value={selectedTags}
                                        onChange={(selected) => setSelectedTags(selected || [])}
                                        options={tagsDisponiveis.map(tag => ({
                                            value: tag.id,
                                            label: tag.nome,
                                            cor: tag.cor
                                        }))}
                                        formatOptionLabel={(option) => (
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="badge px-2 py-1"
                                                    style={{
                                                        backgroundColor: option.cor,
                                                        color: getContrastColor(option.cor),
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {option.label}
                                                </span>
                                            </div>
                                        )}
                                        noOptionsMessage={() => "Nenhuma tag encontrada"}
                                        isClearable
                                        styles={{
                                            multiValue: (base, { data }) => ({
                                                ...base,
                                                backgroundColor: data.cor,
                                                borderRadius: '4px',
                                                padding: '2px 4px',
                                            }),
                                            multiValueLabel: (base, { data }) => ({
                                                ...base,
                                                color: getContrastColor(data.cor),
                                                fontSize: '0.9rem',
                                                padding: '2px 6px',
                                            }),
                                            multiValueRemove: (base, { data }) => ({
                                                ...base,
                                                color: getContrastColor(data.cor),
                                                ':hover': {
                                                    backgroundColor: data.cor,
                                                    color: getContrastColor(data.cor),
                                                    opacity: 0.7
                                                },
                                            }),
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Observações</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        name="observacao"
                                        value={formData.observacao}
                                        autoComplete="off"
                                        maxLength={1000}
                                        onChange={handleInputChange}
                                    />
                                    <small className="text-muted">{formData.observacao.length}/1000</small>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-danger" onClick={handleClose}>
                            <FaTimes className="me-1" />
                            Fechar
                        </button>
                        <button type="button" className="btn btn-custom-primary" onClick={handleSubmit}>
                            <FaRegSave className="me-1" />
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NovoLancamento;